class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
      this.closeStickySearchButton = document.querySelector('.header-search-close');
      this.productToShow = this.dataset.productToShow;
      this.searchDetails = this.querySelector('details.search_details')
      this.isOpen = false;

      this.setupEventListeners();
    }
    
    setupEventListeners() {
      const form = this.querySelector('form.search');
      form.addEventListener('submit', this.onFormSubmit.bind(this));
      
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 500).bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      document.addEventListener('click', this.onDocClick.bind(this));
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();
      
      if (!searchTerm.length) {
        this.close(true);
        return;
      }
      
      this.getSearchResults(searchTerm);
    }
  
    onFormSubmit(event) {
      event.preventDefault();

      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      const selectedLink = this.querySelector('[aria-selected="true"] a');
      if (selectedLink) {
        selectedLink.click();
        return;
      }

      this.submitNativeSearch(searchTerm);
    }

    submitNativeSearch(searchTerm = this.getQuery()) {
      const query = searchTerm.trim();
      if (!query.length) return;

      window.location.assign(`${routes.search_url}?q=${encodeURIComponent(query)}&type=product`);
    }
  
    onFocus() {
      const searchTerm = this.getQuery();
      
      if (!searchTerm.length) return this.showTrendingAndProducts();

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(searchTerm);
      }
    }
  
    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
          this.hideTrendingAndProducts();
        };
      })
    }
    
    onKeyup(event) {
      if (!this.getQuery().length) {
        this.close(true);
        this.showTrendingAndProducts();
      };
      event.preventDefault();
      
      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up')
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }
  
    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (
        event.code === 'ArrowUp' ||
        event.code === 'ArrowDown'
      ) {
        event.preventDefault();
      }

      if (event.code === 'Enter') {
        const selectedLink = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
        if (!selectedLink) {
          event.preventDefault();
          this.submitNativeSearch();
        }
      }
    }
  
    switchOption(direction) {
      if (!this.getAttribute('open')) return;
  
      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');
      const allElements = this.querySelectorAll('li');
      let activeElement = this.querySelector('li');
  
      if (moveUp && !selectedElement) return;
  
      this.statusElement.textContent = '';
      
      if (!moveUp && selectedElement) {
        activeElement = selectedElement.nextElementSibling || allElements[0];
      } else if (moveUp) {
        activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
      }
  
      if (activeElement === selectedElement) return;
  
      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);
  
      this.setLiveRegionText(activeElement.textContent);
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
      
    selectOption() {
      const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
  
      if (selectedProduct) selectedProduct.click();
    }
    
    getSearchResults(searchTerm) {
      const queryKey = searchTerm.toLowerCase().replace(/\s+/g, '-');
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        this.updateViewAllLink(searchTerm);
        return;
      }

      // Use Shopify's normal storefront search, which we verified finds this
      // catalog correctly by title, SKU and barcode.
      const nativeSearchUrl =
        `${routes.search_url}?q=${encodeURIComponent(searchTerm)}` +
        `&type=product&sort_by=relevance&options%5Bprefix%5D=last`;

      fetch(nativeSearchUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');

          // Ella's actual search-result product list.
          const list = doc.querySelector('#main-collection-product-grid');
          const nativeItems = list
            ? Array.from(list.querySelectorAll(':scope > li.product'))
            : [];

          // Only keep list items that really contain a product link.
          const seen = new Set();
          const products = nativeItems.filter((item) => {
            const link = item.querySelector('a[href*="/products/"]');
            if (!link) return false;

            const key = link.getAttribute('href').split('?')[0];
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

          const searchSection = doc.querySelector('[id^="SearchSection"]');
          const totalCount = searchSection?.dataset?.searchCount || products.length;

          let resultsMarkup = '';

          if (products.length) {
            const cards = products
              .slice(0, 10)
              .map((item) => item.outerHTML)
              .join('');

            resultsMarkup = `
              <div id="predictive-search-results" class="quickSearchResults quickSearchResultsBlock custom-scrollbar">
                <div class="container">
                  <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing search-block-title text-left">
                    <span class="text">Search results</span>
                  </h2>

                  <div id="predictive-search-results-list"
                       class="predictive-search__results-list list-unstyled"
                       role="listbox"
                       aria-labelledby="predictive-search-products">
                    <div class="products-grid column-${this.productToShow || 4} disable-srollbar text-left">
                      ${cards}
                    </div>

                    <div id="predictive-search-option-search-keywords"
                         class="predictive-search__list-item productViewAll"
                         role="option">
                      <a href="${nativeSearchUrl}"
                         class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow button button-view-all"
                         tabindex="-1"
                         data-qs-view-all-link>
                        View all results
                      </a>
                    </div>
                  </div>

                  <span class="hidden" data-predictive-search-live-region-count-value>
                    ${totalCount} results
                  </span>
                </div>
              </div>`;
          } else {
            // Never leave a valid partial query looking "dead": provide the
            // native-search action even when the AJAX response has no cards.
            resultsMarkup = `
              <div id="predictive-search-results" class="quickSearchResults quickSearchResultsBlock custom-scrollbar">
                <div class="container">
                  <div id="predictive-search-results-list"
                       class="predictive-search__results-list list-unstyled"
                       role="listbox">
                    <div class="productEmpty text-center">
                      <span class="text">No instant matches yet.</span>
                      <span class="keyword"><strong>${this.escapeHtml(searchTerm)}</strong></span>
                    </div>

                    <div class="predictive-search__list-item productViewAll" role="option">
                      <a href="${nativeSearchUrl}"
                         class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow button button-view-all"
                         tabindex="-1"
                         data-qs-view-all-link>
                        Search all products
                      </a>
                    </div>
                  </div>

                  <span class="hidden" data-predictive-search-live-region-count-value>
                    Search all products
                  </span>
                </div>
              </div>`;
          }

          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
          this.updateViewAllLink(searchTerm);
        })
        .catch((error) => {
          console.error('Native predictive search failed:', error);
          this.close();
        });
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value;
      return div.innerHTML;
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
      
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }
  
    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
      
      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();
      this.hideTrendingAndProducts();
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }
    
    getResultsMaxHeight() {
      this.resultsMaxHeight = window.innerHeight - document.querySelector('[id^="shopify-section-header"]').getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }
    
    open() {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;   
      this.searchDetails.setAttribute('open', true);
    }

    close(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      
      const selected = this.querySelector('[aria-selected="true"]');
  
      if (selected) selected.setAttribute('aria-selected', false);
  
      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false
      this.predictiveSearchResults.removeAttribute('style');
  
      this.isOpen = false;
      this.searchDetails.removeAttribute('open', true);
    }

    showTrendingAndProducts() {
      if (!this.trendingAndProductsBlock) return 
      this.trendingAndProductsBlock.classList.add('is-show')
      this.trendingAndProductsBlock.classList.remove('hidden')
    }

    hideTrendingAndProducts() {
      if (!this.trendingAndProductsBlock) return 

      this.trendingAndProductsBlock.classList.remove('is-show')
      this.trendingAndProductsBlock.classList.add('hidden')
    }

    onDocClick(e) {
      let closeButton;
      if (e.target.matches('.header-search-popup-close') || e.target.matches('.header-search-close')) {
        closeButton = e.target 
      } else if (e.target.closest('.header-search-popup-close')) {
        closeButton = e.target.closest('.header-search-popup-close')
      } else if (e.target.closest('.header-search-close')) {
        closeButton = e.target.closest('.header-search-close')
      } 

      const isInModal = this.contains(e.target) 

      if (isInModal && !closeButton) return;

      this.close();
      this.hideTrendingAndProducts();
    }

    onCloseStickySearchClick() {
      this.close();
      this.hideTrendingAndProducts();
      document.querySelector('body').classList.remove('sticky-search-open')
    }

    updateViewAllLink(searchTerm) {
      const qsViewAllLink = document.querySelector('[data-qs-view-all-link]');
      if (!qsViewAllLink) return 

      const linkTotal = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product`
      
      qsViewAllLink.href = linkTotal
      return this.getTotalResults(linkTotal).then(count => {
        qsViewAllLink.innerHTML = qsViewAllLink.innerHTML.replace('()', `(${count})`)
      });
    }

    getTotalResults(url) {
      return fetch(url)
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }

          return response.text();
        })  
        .then((text) => {
          const totalCount = new DOMParser().parseFromString(text, 'text/html').querySelector('[id^="SearchSection"]').dataset.searchCount;
          return totalCount
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    }
  }
  
  customElements.define('predictive-search', PredictiveSearch);
  